import { BaseEvent, BaseSystemMessage } from '../../types';
import { ChatTriggerMatchSource, MatchedChatTrigger, ResolveTrigger, TriggerMatchListener } from './ChatTrigger.types';
import * as PleasedApi from '@/common/api/api.pleased';
import { getChatTriggerReport } from './ChatTrigger.utils';
import { noop } from '../ChatFramework.utils';
import { TransactionTypeName } from '@/common/transaction-utils';
import { TransStatement } from '@/common/api/model';
import { logger } from '@/common/api/logger';

let checkedTrigger: { type: ChatTriggerMatchSource; id: number | string } | null = null;

export async function checkTrigger(
  {
    type,
    id,
    triggerEvent,
    triggerMessage,
    userCountry,
  }: {
    type: ChatTriggerMatchSource;
    id: number | string;
    triggerEvent: BaseEvent | null;
    triggerMessage: BaseSystemMessage | null;
    userCountry: string;
  },
  onMatch: TriggerMatchListener
): Promise<void | MatchedChatTrigger> {
  checkedTrigger = { type, id };
  return PleasedApi.matchOverlayTrigger
    .cached({ type, id: +id, userCountry })
    .then((matchedTrigger) => {
      if (!matchedTrigger?.message?.length) return;
      const { chatTriggerId } = matchedTrigger;

      let resolveTrigger: ResolveTrigger | undefined;
      const triggerResolver = new Promise<'END_CHAT' | 'CONTINUE'>((resolve) => {
        resolveTrigger = resolve;
      });

      const trigger = {
        ...matchedTrigger,
        matchSource: type,
        triggerEvent,
        triggerMessage,
        report: getChatTriggerReport({ chatTriggerId, resolveTrigger }),
        waitForResolution: () => triggerResolver,
      };

      onMatch(trigger);

      return trigger;
    })
    .catch(noop);
}

export const manualTriggerCheck = checkTrigger;

export async function checkTransactionTrigger(
  {
    userCountry,
    transactionType,
    transaction,
  }: {
    userCountry: string;
    transactionType: TransactionTypeName;
    transaction: TransStatement;
  },
  onMatch: TriggerMatchListener
): Promise<void | MatchedChatTrigger> {
  const { type, id } = checkedTrigger || {};
  if (!type || !id) {
    logger.info('No trigger context available for transaction trigger check', { type, id });
    return;
  }
  return PleasedApi.matchTransactionTrigger({
    userCountry,
    transactionType,
    transaction,
    type,
    id: +id,
  })
    .then((matchedTrigger) => {
      if (!matchedTrigger?.message?.length) return;
      const { chatTriggerId } = matchedTrigger;

      let resolveTrigger: ResolveTrigger | undefined;
      const triggerResolver = new Promise<'END_CHAT' | 'CONTINUE'>((resolve) => {
        resolveTrigger = resolve;
      });

      const trigger = {
        ...matchedTrigger,
        report: getChatTriggerReport({ chatTriggerId, resolveTrigger }),
        waitForResolution: () => triggerResolver,
        triggerMessage: null,
        triggerEvent: null,
        matchSource: null,
      };

      onMatch(trigger);

      return trigger;
    })
    .catch(noop);
}

export const manualTransactionTriggerCheck = checkTransactionTrigger;
